import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";

import store from "../../../../store";
import { API_URL, CONVERSATION_PURPOSE_KEY } from "../../../common/constants";
import axios from "../../../common/axios";
import { deduplicatedRequest } from "../../../common/requestDeduplication";
import { handleApiError } from "../../../common/errorHandling";
import { cachedRequest } from "../../../common/apiCache";

import {
  CONVERSATION_HISTORY_FAILED,
  CLEAR_CONVERSATION_HISTORY_ERROR,
} from "./types";
import Actions from "./actions";
import {
  JUMP_WINDOW_SIZE,
  bumpLogGeneration,
  logGeneration,
  toChatMessage,
} from "./historyWindow";

function conversationOpen(id, users, callback = null, errorCallback = null) {
  return function (dispatch) {
    return axios
      .post(`${API_URL}/v1/chat/conversation.open`, {
        conversation: id,
        users: users,
      })
      .then(({ data }) => {
        dispatch(Actions.setConversationId(id));
        if (typeof callback === "function") {
          callback();
        }
      })
      .catch((error) => {
        // Log the error for debugging
        console.error("Failed to open conversation:", error);

        // Don't show alert to user - we'll handle this more gracefully
        console.log("Handling conversation.open failure gracefully...");

        // Call error callback if provided
        if (typeof errorCallback === "function") {
          errorCallback(error);
        }

        // Track error but mark it as being handled gracefully
        Sentry.withScope((scope) => {
          scope.setTag("handled", "gracefully");
          Sentry.captureException(
            new Error(
              `Attempting to recover from conversation.open failure: ${
                error?.message || "Unknown error"
              }`
            )
          );
        });
      });
  };
}

// Track the last time conversationList was called to implement cooldown
let lastConversationListCall = 0;
const CONVERSATION_LIST_COOLDOWN = 1000; // 1 seconds minimum between calls

const CONVERSATION_LIST_LIMIT = 25;

/**
 * Fetch list of conversations with deduplication and cooldown.
 *
 * @param {Object} socket - Socket.io connection
 * @param {Function} callback - Callback function for result
 * @param {string} filter - Optional filter for conversations. When omitted,
 *   automatically uses the active filter stored in Redux so that socket
 *   refreshes and other background fetches respect the user's selection.
 * @param {Object} options - Additional options
 * @param {boolean} options.forceRefresh - Skip cooldown and cache to force a fresh API call
 * @param {boolean} options.includeGroups - Include the groups pipeline.
 *   Defaults to true. Set to false to fetch DMs only.
 * @param {boolean} options.includeDMs - Include the DMs pipeline.
 *   Defaults to true. Set to false to fetch groups only.
 * @param {number} options.page - Server page (0-indexed). Defaults to 0.
 * @param {boolean} options.append - When true, append results via
 *   APPEND_CONVERSATIONS (de-dup by conversationId) instead of replacing
 *   the list. Also skips the cooldown — Load More clicks shouldn't be
 *   blocked by a recent initial fetch.
 * @returns {Function} - Redux thunk function
 */
function conversationList(socket, callback = null, filter, options = {}) {
  return function (dispatch) {
    const now = Date.now();
    const {
      forceRefresh = false,
      includeGroups = true,
      includeDMs = true,
      page = 0,
      append = false,
    } = options;
    // Mirror the server-side fallback: a request for neither section is
    // meaningless, so treat it as "both".
    const wantGroups = !includeGroups && !includeDMs ? true : includeGroups;
    const wantDMs = !includeGroups && !includeDMs ? true : includeDMs;
    const groupsOnly = wantGroups && !wantDMs;
    const dmsOnly = !wantGroups && wantDMs;

    // When no explicit filter is passed, use the active filter from Redux
    // so that socket-triggered refreshes respect the user's current selection.
    if (filter === undefined) {
      const reduxFilter = store.getState().chat?.conversationFilter;
      if (reduxFilter) {
        filter = [reduxFilter];
      }
    }

    // Cooldown protects the initial / forced-refresh paths from rapid-fire
    // background callers (sockets, hooks). Load More (`append`) bypasses it
    // — the user is explicitly asking for the next page, and an append
    // can't clobber state that another fetch is about to load.
    if (
      !forceRefresh &&
      !append &&
      now - lastConversationListCall < CONVERSATION_LIST_COOLDOWN
    ) {
      console.debug(
        `Skipping conversationList call - on cooldown (${
          now - lastConversationListCall
        }ms < ${CONVERSATION_LIST_COOLDOWN}ms)`
      );

      // Still call callback with previous data if callback provided
      if (typeof callback === "function") {
        const state = store.getState();
        const conversations = state.chat?.conversations || [];
        callback(conversations, {
          hasError: false,
          error: "",
          fromCache: true,
        });
      }

      return Promise.resolve();
    }

    // Update the timestamp for cooldown tracking (only on non-append paths
    // so Load More doesn't shift the cooldown window for refreshes).
    if (!append) lastConversationListCall = now;

    // Cache + dedup key must include section flags and page so page 1
    // doesn't serve page 0's cached payload (or vice versa).
    const dedupKey = `conversation.list-${wantGroups ? 1 : 0}${
      wantDMs ? 1 : 0
    }-${filter || "all"}-${page}`;

    // Flip the per-section loading flag for Load More UX (button spinner +
    // disable). For initial / replace fetches the consumer manages its
    // own `isLoading` state, so we skip the global flag there.
    if (append && groupsOnly) {
      dispatch(
        Actions.setConversationPagination({ isLoadingMoreGroups: true })
      );
    } else if (append && dmsOnly) {
      dispatch(Actions.setConversationPagination({ isLoadingMoreDMs: true }));
    }

    const clearLoadingFlag = () => {
      if (append && groupsOnly) {
        dispatch(
          Actions.setConversationPagination({ isLoadingMoreGroups: false })
        );
      } else if (append && dmsOnly) {
        dispatch(
          Actions.setConversationPagination({ isLoadingMoreDMs: false })
        );
      }
    };

    // Create a timeout to handle stalled requests
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(
          new Error("Request timeout - conversation list fetch took too long")
        );
      }, 15000); // 15-second timeout
    });

    // Backend uses optimistic flag parsing: a single explicit `isGroup=true`
    // (or `isDM=true`) implies the other section is excluded. So when
    // targeting a single section we only need to send the affirmative flag;
    // when both are wanted (default "all" load) we omit them entirely so
    // the historical URL is unchanged.
    let sectionParams = "";
    if (wantGroups && !wantDMs) sectionParams = "&isGroup=true";
    else if (!wantGroups && wantDMs) sectionParams = "&isDM=true";

    // Use stale-while-revalidate caching for better performance
    const fetchPromise = cachedRequest(
      dedupKey,
      () => {
        // Use deduplication to prevent duplicate concurrent calls
        return deduplicatedRequest(dedupKey, () => {
          return axios
            .get(
              `${API_URL}/v1/chat/conversation.list?page=${page}&limit=${CONVERSATION_LIST_LIMIT}${
                filter && filter.length > 0
                  ? "&filter=" + filter.toString()
                  : ""
              }${sectionParams}`
            )
            .then(async (res) => {
              if (res?.data) {
                const results = res.data.conversations?.results || [];
                const totalGroups =
                  res.data.conversations?.totalGroups ??
                  store.getState().chat?.conversationPagination?.totalGroups ??
                  0;
                const totalDMs =
                  res.data.conversations?.totalDMs ??
                  store.getState().chat?.conversationPagination?.totalDMs ??
                  0;

                if (append) {
                  dispatch(Actions.appendConversations(results));
                } else {
                  dispatch(Actions.setConversations(results));
                }

                // Recompute pagination after the dispatch so loaded counts
                // reflect the just-merged list. `hasMore` is true when
                // either (a) totals say there are more, AND (b) the last
                // page wasn't shorter than the limit (i.e. the server
                // actually has more to give — guards against stale totals
                // when filters narrow the result).
                const stateAfter = store.getState().chat || {};
                const loadedGroups = (stateAfter.conversations || []).filter(
                  (c) => c?.conversation?.isGroup
                ).length;
                const loadedDMs = (stateAfter.conversations || []).filter(
                  (c) => c?.conversation?.isDM
                ).length;
                const groupsInResponse = results.filter(
                  (c) => c?.conversation?.isGroup
                ).length;
                const dmsInResponse = results.filter(
                  (c) => c?.conversation?.isDM
                ).length;

                const paginationUpdate = {
                  totalGroups,
                  totalDMs,
                };

                // `hasMore` uses both totals AND `responseCount === limit`:
                // totals can be stale across paginated fetches (filter
                // narrowing, deletions), and a short page is a definitive
                // signal that the server has nothing more for that section.
                if (wantGroups) {
                  paginationUpdate.groupsServerPage = page;
                  paginationUpdate.hasMoreGroups =
                    loadedGroups < totalGroups &&
                    groupsInResponse === CONVERSATION_LIST_LIMIT;
                }
                if (wantDMs) {
                  paginationUpdate.dmsServerPage = page;
                  paginationUpdate.hasMoreDMs =
                    loadedDMs < totalDMs &&
                    dmsInResponse === CONVERSATION_LIST_LIMIT;
                }

                dispatch(Actions.setConversationPagination(paginationUpdate));

                // Subscribe to any newly-arrived rooms. joinAllRooms is
                // idempotent (skips rooms already joined via
                // listOwnSockets) so calling it after each append is safe.
                if (results.length) {
                  console.debug("joined from operations.jsx conversationList");
                  try {
                    await joinAllRooms(socket, results);
                  } catch (joinError) {
                    console.error("Error joining rooms:", joinError);
                    // Continue anyway - don't let join errors prevent the UI from updating
                  }
                }
                if (typeof callback === "function") {
                  callback(results, {
                    hasError: false,
                    error: "",
                  });
                }
              }
              clearLoadingFlag();
              return res;
            })
            .catch((error) => {
              // Use centralized error handling
              handleApiError("conversation.list", error);
              clearLoadingFlag();

              // Make sure to call callback even on error
              if (typeof callback === "function") {
                callback(null, {
                  hasError: true,
                  error:
                    error?.response?.message ||
                    "Unable to sync chats at this time",
                });
              }

              throw error; // Rethrow for promise chaining
            });
        });
      },
      {
        ttl: 3000, // Consider conversations stale after 3 seconds
        staleTtl: 10000, // Expire after 10 seconds
        forceRefresh,
      }
    );

    // Race the fetch against the timeout
    return Promise.race([fetchPromise, timeoutPromise]).catch((error) => {
      console.error("Error in conversationList:", error);
      clearLoadingFlag();

      // Make sure callback is always called, even for unexpected errors or timeouts
      if (typeof callback === "function") {
        callback(null, {
          hasError: true,
          error:
            error?.message || "Failed to load conversations - please try again",
        });
      }

      // Don't throw error further to prevent unhandled promise rejections
      return null;
    });
  };
}

function conversationListTopics(
  conversationId,
  page = 0,
  limit = 50,
  search = "",
  callback = null
) {
  return function (dispatch) {
    dispatch(Actions.loadingConversationTopics(conversationId, true));
    return axios
      .get(
        `${API_URL}/v1/chat/conversation.listTopics?conversationId=${conversationId}&search=${search}&page=${page}&limit=${limit}`
      )
      .then((res) => {
        if (res.data) {
          const { topics, currentCount, totalCount } = res.data;
          dispatch(
            Actions.setConversationTopicList(
              conversationId,
              topics,
              currentCount,
              page,
              totalCount
            )
          );
          if (typeof callback === "function") {
            callback(conversationId, topics);
          }
        }
      })
      .catch((error) => {
        error?.response?.message
          ? alert(error.response.message)
          : console.log("Sorry, but we are unable to sync with backend");
      })
      .finally(() => {
        dispatch(Actions.loadingConversationTopics(conversationId, false));
      });
  };
}

function conversationCreateTopic(conversationId, topic, callBack) {
  return function (dispatch) {
    return axios
      .post(`${API_URL}/v1/chat/conversation.createTopic`, {
        conversationId,
        ...topic,
      })
      .then((res) => {
        if (res.data && res.data.topic) {
          dispatch(conversationListTopics(conversationId, 0, 50, "", null));
        }

        const topicId = res.data.topic._id;
        if (typeof callBack === "function") {
          callBack(topicId);
        }
      })
      .catch((error) => {
        error?.response?.message
          ? alert(error.response.message)
          : console.log("Sorry, but we are unable to sync with backend");
      });
  };
}

/**
 * Check if conversation list needs updating based on room membership
 * Improved with deduplication and better error handling
 *
 * @param {Object} socket - Socket.io connection
 * @param {string} roomId - Room/conversation ID to check
 * @returns {Function} - Redux thunk function
 */
function shouldUpdateConversationList(socket, roomId) {
  return function (dispatch) {
    // Don't attempt if socket isn't available
    if (!socket || socket.disconnected) {
      console.debug("Cannot check room membership - socket disconnected");
      return Promise.resolve();
    }

    // Create a unique key for this request
    const dedupKey = `shouldUpdateConversationList-${roomId}`;

    // Use deduplication to prevent multiple concurrent checks
    return deduplicatedRequest(dedupKey, () => {
      return new Promise((resolve) => {
        // Set a timeout to prevent hanging if socket doesn't respond
        const timeoutId = setTimeout(() => {
          console.debug(
            "Socket listOwnSockets timed out, proceeding with conversation list update"
          );
          dispatch(conversationList(socket));
          resolve();
        }, 5000);

        socket.emit("listOwnSockets", (err, data) => {
          // Clear timeout since we got a response
          clearTimeout(timeoutId);

          // Handle error cases gracefully
          if (err) {
            console.debug(
              "Error checking room membership, proceeding with conversation list update",
              err
            );
            dispatch(conversationList(socket));
            return resolve();
          }

          // Ensure data is valid
          if (!data || !data[socket.id]) {
            console.debug(
              "Invalid socket data, proceeding with conversation list update"
            );
            dispatch(conversationList(socket));
            return resolve();
          }

          const joinedRooms = data[socket.id];

          // Only fetch conversations if we're not already in this room
          if (!joinedRooms.includes(roomId)) {
            console.log(
              "[c] - shouldUpdateConversationList Fetching conversation list"
            );
            dispatch(conversationList(socket));
          } else {
            console.debug(
              `Already joined room ${roomId}, skipping conversation list update`
            );
          }

          resolve();
        });
      });
    });
  };
}

/**
 * Load a page of a topic's history into its chat log.
 *
 * - no cursor: the latest page; replaces the log and re-attaches its window.
 * - `cursor` + direction `lte`/`gte`: legacy timestamp page, merged in.
 * - `{ cursorId }` + direction `lt`/`gt`: keyset page strictly older/newer
 *   than that loaded message (no gaps or duplicates), merged in; the response
 *   says whether more exist in that direction.
 */
function conversationHistory(
  conversationId,
  topicId,
  eventId,
  cursor,
  direction,
  limit = 30,
  { cursorId } = {}
) {
  return function (dispatch) {
    dispatch(Actions.setMessageLoadingState(conversationId, true, topicId));
    const query = new URLSearchParams({ conversation: conversationId });
    if (topicId) query.set("topicId", topicId);
    if (eventId) query.set("eventId", eventId);
    query.set("limit", limit);
    if (cursorId) query.set("cursorId", cursorId);
    else if (cursor) query.set("cursor", cursor);
    if (direction) query.set("direction", direction);
    const isPage = Boolean(cursor || cursorId);
    // A page belongs to the log it was requested from; the latest page
    // replaces the log, so it starts a new one.
    const generation = isPage
      ? logGeneration(conversationId, topicId)
      : bumpLogGeneration(conversationId, topicId);
    return axios
      .get(`${API_URL}/v1/chat/conversation.history?${query}`)
      .then((res) => {
        // The log was replaced (jump / jump to latest) while this was in
        // flight: merging it would splice a stale page and its flags in.
        if (generation !== logGeneration(conversationId, topicId)) return;
        // A keyset page only continues the log from the message it was
        // requested from. A page requested from a window that has since been
        // replaced (e.g. by the scroll that "jump to latest" itself causes,
        // which can fire after the replace started) no longer fits.
        if (cursorId) {
          const log = topicId
            ? store.getState().chat?.chatLogs?.[conversationId]?.[topicId]
            : store.getState().chat?.chatLogs?.[conversationId];
          const edge =
            direction === "gt" ? log?.[log.length - 1] : log?.[0];
          if (!Array.isArray(log) || edge?.messageId !== cursorId) return;
        }
        // Clear any existing error state for this conversation when we get a successful response
        dispatch({
          type: CLEAR_CONVERSATION_HISTORY_ERROR,
          payload: {
            conversationId,
            topicId,
          },
        });

        if (res.data && res.data.history) {
          if (!isPage) {
            // The latest page: whatever window was there, the log now ends
            // at the newest message.
            dispatch(
              Actions.setHistoryWindow(conversationId, topicId, {
                hasNewer: false,
                pendingNewer: 0,
                anchorId: null,
              })
            );
            dispatch(
              Actions.setAllMessagesAreLoaded(conversationId, topicId, false)
            );
          }
          if (cursorId && direction === "gt") {
            dispatch(
              Actions.setHistoryWindow(
                conversationId,
                topicId,
                res.data.hasNewer
                  ? { hasNewer: true }
                  : { hasNewer: false, pendingNewer: 0 }
              )
            );
          }
          if (!cursorId && cursor && direction === "gte") {
            // Legacy unread page (lastSeen onwards): a full page means the
            // log may still end before the latest message.
            dispatch(
              Actions.setHistoryWindow(
                conversationId,
                topicId,
                res.data.history.length >= limit
                  ? { hasNewer: true }
                  : { hasNewer: false, pendingNewer: 0 }
              )
            );
          }
          if (cursorId && direction === "lt" && res.data.hasOlder === false) {
            dispatch(Actions.setAllMessagesAreLoaded(conversationId, topicId));
          }

          if (res.data.history.length) {
            const historyMessages = res.data.history.map(toChatMessage);
            if (!isPage) {
              dispatch(
                Actions.setRoomMessages(
                  conversationId,
                  historyMessages,
                  topicId
                )
              );
            } else {
              dispatch(
                Actions.updateRoomHistory(
                  conversationId,
                  historyMessages,
                  topicId,
                  direction
                )
              );
            }
          } else if (!direction || direction === "lte" || direction === "lt") {
            // Only mark all-loaded for backward (lte) direction.
            // Forward (gte) empty response means no newer messages yet,
            // but should NOT block loading older messages via scroll-up.
            dispatch(Actions.setAllMessagesAreLoaded(conversationId, topicId));
          }
        }
      })
      .catch((error) => {
        // Dispatch an action to indicate that message fetching failed
        dispatch({
          type: CONVERSATION_HISTORY_FAILED,
          payload: {
            conversationId,
            topicId,
            error:
              error?.response?.data?.message ||
              "Failed to load messages. Please try again later.",
          },
        });

        // Show toast message as well
        toast.error(
          error?.response?.data?.message ||
            "Failed to load messages. Please try again later."
        );

        // Log to console for debugging
        console.error("Failed to fetch conversation history:", error);

        // Track error in Sentry
        Sentry.captureException(error);
      })
      .finally(() => {
        dispatch(
          Actions.setMessageLoadingState(conversationId, false, topicId)
        );
      });
  };
}

/**
 * Replace a topic's log with a window of history centred on `messageId`, so a
 * quote, search hit or deeplink lands on its message no matter how far back
 * it is. Scrolling from there pages older/newer with keyset cursors.
 *
 * Resolves to true when the message is now in the log; false (with a toast)
 * when it isn't in this topic, was deleted, or the user can't read it.
 */
function jumpToMessage(conversationId, topicId, messageId) {
  return async function (dispatch) {
    dispatch(Actions.setMessageLoadingState(conversationId, true, topicId));
    const generation = bumpLogGeneration(conversationId, topicId);
    try {
      const query = new URLSearchParams({
        conversation: conversationId,
        around: messageId,
        limit: JUMP_WINDOW_SIZE,
      });
      if (topicId) query.set("topicId", topicId);
      const { data } = await axios.get(
        `${API_URL}/v1/chat/conversation.history?${query}`
      );
      // Superseded by a later jump or jump-to-latest.
      if (generation !== logGeneration(conversationId, topicId)) return false;
      dispatch(
        Actions.setRoomMessages(
          conversationId,
          (data.history || []).map(toChatMessage),
          topicId
        )
      );
      dispatch(
        Actions.setAllMessagesAreLoaded(conversationId, topicId, !data.hasOlder)
      );
      dispatch(
        Actions.setHistoryWindow(conversationId, topicId, {
          hasNewer: Boolean(data.hasNewer),
          pendingNewer: 0,
          anchorId: data.anchorId,
        })
      );
      return true;
    } catch (error) {
      if (error?.response?.status === 404) {
        toast.error("This message is no longer available.");
      } else {
        toast.error("Couldn't open that message. Please try again.");
        Sentry.captureException(error);
      }
      return false;
    } finally {
      dispatch(Actions.setMessageLoadingState(conversationId, false, topicId));
    }
  };
}

/** Back to the newest messages after a jump (replaces the log). */
function jumpToLatest(conversationId, topicId) {
  return conversationHistory(conversationId, topicId);
}

function conversationMark(conversationId, timestamp = Date.now()) {
  return function (dispatch) {
    return axios
      .post(`${API_URL}/v1/chat/conversation.mark`, {
        conversation: conversationId,
        ts: timestamp,
      })
      .then((res) => {
        if (res?.data) {
          console.log("updated participants last seen");
          if (res.data.updatedConversation) {
            dispatch(
              Actions.updateConversation({
                conversationId: res.data.updatedConversation.conversationId,
                conversation: res.data.updatedConversation,
              })
            );
          }
        }
      })
      .catch((error) => {
        error?.response?.message
          ? alert(error.response.message)
          : console.log("Sorry, but we are unable to sync with backend");
      });
  };
}

function conversationMarkUnread(conversationId) {
  return function (dispatch) {
    return axios
      .post(`${API_URL}/v1/chat/conversation.markUnread`, {
        conversation: conversationId,
      })
      .then((res) => {
        if (res?.data?.updatedConversation) {
          console.log("marked conversation as unread");
          dispatch(
            Actions.updateConversation({
              conversationId: res.data.updatedConversation.conversationId,
              conversation: res.data.updatedConversation,
            })
          );
        }
      })
      .catch((error) => {
        error?.response?.message
          ? alert(error.response.message)
          : console.log(
              "Sorry, but we are unable to mark conversation as unread"
            );
      });
  };
}

function fetchConversationEmailParticipants({
  conversationId,
  limit = 50,
  skip = 0,
  participantIds = [],
}) {
  return function (dispatch) {
    return axios
      .post(`${API_URL}/v1/game-player/profile/multiple`, {
        gamePlayerIds: participantIds,
        select:
          "firstName lastName avatar gamePlayerId userName defaultCredential credentials isApprovedGigProvider",
      })
      .then((res) => {
        if (res?.data) {
          const participants = res.data.map((participant) => {
            return {
              isAdmin: false,
              _id: participant.gamePlayerId,
              id: participant.gamePlayerId,
              participantId: participant.gamePlayerId,
              conversationId,
              profile: participant,
            };
          });
          dispatch(
            Actions.setParticipantsDetails(conversationId, participants || [])
          );
        }
      })
      .catch((error) => {
        error?.response?.message
          ? alert(error.response.message)
          : console.log("Sorry, but we are unable to sync with backend");
      });
  };
}

function conversationParticipants({
  conversationId,
  limit = 50,
  skip = 0,
  participantIds = [],
  search = "",
}) {
  return function (dispatch) {
    const payload = {
      limit,
      skip,
      participantIds,
      conversation: conversationId,
      fetchProfile: true,
    };

    if (search?.length) {
      payload["search"] = search;
    }
    return axios
      .get(`${API_URL}/v1/chat/conversation.participants`, {
        params: payload,
      })
      .then((res) => {
        if (res?.data) {
          dispatch(
            Actions.setParticipantsDetails(
              conversationId,
              res.data.participants || []
            )
          );
          return res.data;
        }
      })
      .catch((error) => {
        error?.response?.message
          ? alert(error.response.message)
          : console.log("Sorry, but we are unable to sync with backend");
      });
  };
}

function conversationParticipantsCount(conversationId, callback = null) {
  return (dispatch) => {
    return axios
      .get(
        `${API_URL}/v1/chat/conversation.participantsCount?conversation=${conversationId}`
      )
      .then((res) => {
        if (res?.data?.success) {
          dispatch(
            Actions.setParticipantsCount(conversationId, res.data.count)
          );
          if (typeof callback === "function") {
            callback(res.data.count);
          }
        }
      })
      .catch((error) => {
        error?.response?.message
          ? alert(error.response.message)
          : console.log("Sorry, but we are unable to sync with backend");
      });
  };
}

// Track if joinAllRooms is already executing to prevent multiple concurrent calls
let joinAllRoomsInProgress = false;

async function joinAllRooms(socket, allConversations) {
  // Guard against concurrent executions
  if (joinAllRoomsInProgress) {
    console.debug("joinAllRooms already in progress, skipping duplicate call");
    return Promise.resolve();
  }

  // Guard against empty/invalid inputs
  if (!socket || socket.disconnected) {
    console.debug("Cannot join rooms - socket is disconnected or null");
    return Promise.resolve();
  }

  if (!allConversations || !Array.isArray(allConversations)) {
    console.debug("Cannot join rooms - invalid conversations list");
    return Promise.resolve();
  }

  // Set the lock flag
  joinAllRoomsInProgress = true;

  // Filter out email conversations
  let conversations = allConversations.filter(
    (c) =>
      c &&
      c.conversationId &&
      c?.purpose?.key !== CONVERSATION_PURPOSE_KEY.EMAIL
  );

  return new Promise((resolve, reject) => {
    // Get list of rooms already joined
    socket.emit("listOwnSockets", async (err, data) => {
      try {
        if (err) {
          console.error("Error listing own sockets:", err);
          // Only show reload prompt in production environment
          if (process.env.NODE_ENV === "production") {
            let reload = window.confirm(
              "There was an error syncing your messages. Reload?"
            );
            if (reload) {
              window.location.reload();
            }
          }
          return resolve(); // Resolve anyway to prevent hanging promises
        }

        // Make sure data is valid
        if (!data || !data[socket.id]) {
          console.error("Invalid data returned from listOwnSockets");
          return resolve();
        }

        const joinedRooms = data[socket.id] || [];
        let subscriptions = [];

        // Only try to join rooms we're not already in
        for (let conversation of conversations) {
          if (
            conversation?.conversationId &&
            !joinedRooms.includes(conversation.conversationId)
          ) {
            subscriptions.push(joinRoom(socket, conversation.conversationId));
          }
        }

        // If we have no subscriptions to make, resolve immediately
        if (subscriptions.length === 0) {
          console.debug(
            "All rooms already joined, no new subscriptions needed"
          );
          return resolve();
        }

        console.debug(`Attempting to join ${subscriptions.length} rooms`);
        await Promise.all(subscriptions);
        resolve();
      } catch (error) {
        console.error("Error in joinAllRooms:", error);
        Sentry.withScope((scope) => {
          scope.setExtra("What Happened?", "Error in joinAllRooms");
          Sentry.captureException(error);
        });
        reject("Unable to join one or more rooms for real time updates.");
      } finally {
        // Always release the lock, even if there was an error
        joinAllRoomsInProgress = false;
      }
    });
  });
}

// Not to be Dispatched
// Used when roomJoin Err Occurs
// Keep track of recent room join exception calls to prevent infinite loops
const roomJoinExceptionAttempts = {};
const RETRY_COOLDOWN_MS = 60000; // 60 seconds cooldown between retries (increased from 30s)
const MAX_RETRY_ATTEMPTS = 1; // Maximum number of retries per room

// Track rooms with persistent join failures to implement circuit breaking
const persistentFailureRooms = new Set();
const CIRCUIT_BREAKER_RESET_TIME = 3600000; // 1 hour - time before we try a failed room again

/**
 * Handle room join exceptions with circuit breaker pattern
 *
 * @param {string} conversationId - ID of the conversation/room
 * @param {string} errCode - Error code from the socket connection
 * @returns {Promise<Object|null>} - Promise resolving to API response or null
 */
const createRoomInChatService = async (conversationId, errCode) => {
  const now = Date.now();

  // Circuit breaker pattern - if this room has had persistent failures, don't try again
  if (persistentFailureRooms.has(conversationId)) {
    console.debug(
      `Skipping retry for room ${conversationId} - circuit breaker open`
    );
    return null;
  }

  // Initialize tracking for this conversation if it doesn't exist
  if (!roomJoinExceptionAttempts[conversationId]) {
    roomJoinExceptionAttempts[conversationId] = {
      lastAttempt: 0,
      count: 0,
      firstFailureTime: now,
    };
  }

  const attemptData = roomJoinExceptionAttempts[conversationId];

  // Check if we're in cooldown period or have exceeded max retries
  if (attemptData.count >= MAX_RETRY_ATTEMPTS) {
    // If we've reached max retries, add to persistent failure set
    persistentFailureRooms.add(conversationId);

    // Schedule removal from persistent failures after reset time
    setTimeout(() => {
      persistentFailureRooms.delete(conversationId);
      // Reset attempt counter
      if (roomJoinExceptionAttempts[conversationId]) {
        roomJoinExceptionAttempts[conversationId].count = 0;
      }
    }, CIRCUIT_BREAKER_RESET_TIME);

    console.debug(
      `Reached maximum retry attempts (${MAX_RETRY_ATTEMPTS}) for room ${conversationId}, circuit breaker engaged`
    );

    // Track circuit breaker activation in Sentry
    Sentry.withScope((scope) => {
      scope.setTag("circuit_breaker", "activated");
      scope.setTag("conversationId", conversationId);
      scope.setLevel("warning");
      Sentry.captureMessage(
        `Chat room circuit breaker activated for ${conversationId}`
      );
    });

    return null;
  }

  if (now - attemptData.lastAttempt < RETRY_COOLDOWN_MS) {
    console.debug(
      `Skipping retry for room ${conversationId} - in cooldown period`
    );
    return null;
  }

  // Update the attempt tracking data
  attemptData.lastAttempt = now;
  attemptData.count++;

  try {
    console.debug(
      `Attempting to fix room join (attempt ${attemptData.count}/${MAX_RETRY_ATTEMPTS}) for ${conversationId}`
    );

    // Use deduplication to prevent multiple concurrent calls
    const dedupKey = `handleRoomJoinException-${conversationId}`;

    return await deduplicatedRequest(dedupKey, async () => {
      const result = await axios.post(
        `${API_URL}/v1/chat/conversation.handleRoomJoinException`,
        {
          conversationId,
          errCode,
        }
      );

      // If successful, reset the counter
      if (result?.data?.success) {
        attemptData.count = 0;
      }

      return result;
    });
  } catch (err) {
    // Use centralized error handling
    handleApiError("conversation.handleRoomJoinException", err);
    console.debug("Not able to Join Room", err);
    return null;
  }
};

// Batch processing for room join exceptions
let roomExceptionQueue = [];
let batchTimeout = null;
const BATCH_DELAY = 500; // Wait 500ms to collect exceptions before processing
const MAX_BATCH_SIZE = 50; // Maximum exceptions per batch

const createRoomInChatServiceBatch = async (conversationId, errCode) => {
  const now = Date.now();

  // Circuit breaker pattern - if this room has had persistent failures, don't try again
  if (persistentFailureRooms.has(conversationId)) {
    console.debug(
      `Skipping batch retry for room ${conversationId} - circuit breaker open`
    );
    return null;
  }

  // Initialize tracking for this conversation if it doesn't exist
  if (!roomJoinExceptionAttempts[conversationId]) {
    roomJoinExceptionAttempts[conversationId] = {
      lastAttempt: 0,
      count: 0,
      firstFailureTime: now,
    };
  }

  const attemptData = roomJoinExceptionAttempts[conversationId];

  // Check if we're in cooldown period or have exceeded max retries
  if (attemptData.count >= MAX_RETRY_ATTEMPTS) {
    persistentFailureRooms.add(conversationId);
    setTimeout(() => {
      persistentFailureRooms.delete(conversationId);
      if (roomJoinExceptionAttempts[conversationId]) {
        roomJoinExceptionAttempts[conversationId].count = 0;
      }
    }, CIRCUIT_BREAKER_RESET_TIME);

    console.debug(
      `Reached maximum retry attempts (${MAX_RETRY_ATTEMPTS}) for room ${conversationId}, circuit breaker engaged`
    );
    return null;
  }

  if (now - attemptData.lastAttempt < RETRY_COOLDOWN_MS) {
    console.debug(
      `Skipping batch retry for room ${conversationId} - in cooldown period`
    );
    return null;
  }

  // Update attempt tracking
  attemptData.lastAttempt = now;
  attemptData.count++;

  // Add to batch queue
  addToExceptionQueue(conversationId, errCode, attemptData);
  return { message: "queued_for_batch" };
};

const addToExceptionQueue = (conversationId, errCode, attemptData) => {
  // Check if this conversation is already queued
  const existingIndex = roomExceptionQueue.findIndex(
    (item) => item.conversationId === conversationId
  );

  if (existingIndex !== -1) {
    // Update existing entry
    roomExceptionQueue[existingIndex] = {
      conversationId,
      errCode,
      attemptData,
    };
  } else {
    // Add new entry
    roomExceptionQueue.push({ conversationId, errCode, attemptData });
  }

  console.debug(
    `Queued room join exception for ${conversationId} (${roomExceptionQueue.length} in queue)`
  );

  // Process immediately if batch is full
  if (roomExceptionQueue.length >= MAX_BATCH_SIZE) {
    processBatchExceptions();
    return;
  }

  // Set/reset timer for batch processing
  if (batchTimeout) {
    clearTimeout(batchTimeout);
  }

  batchTimeout = setTimeout(() => {
    processBatchExceptions();
  }, BATCH_DELAY);
};

const processBatchExceptions = async () => {
  if (roomExceptionQueue.length === 0) return;

  const currentBatch = [...roomExceptionQueue];
  roomExceptionQueue = [];

  if (batchTimeout) {
    clearTimeout(batchTimeout);
    batchTimeout = null;
  }

  console.debug(
    `Processing batch of ${currentBatch.length} room join exceptions`
  );

  try {
    const exceptions = currentBatch.map((item) => ({
      conversationId: item.conversationId,
      errCode: item.errCode,
    }));

    const dedupKey = `handleRoomJoinExceptionBatch-${Date.now()}-${Math.random()}`;

    const result = await deduplicatedRequest(dedupKey, async () => {
      return await axios.post(
        `${API_URL}/v1/chat/conversation.handleRoomJoinExceptionBatch`,
        { exceptions }
      );
    });

    if (result?.data?.summary) {
      const { successful, failed, total } = result.data.summary;
      console.debug(
        `Batch processing completed: ${successful}/${total} successful, ${failed} failed`
      );

      // Reset attempt counters for successful operations
      if (result.data.results) {
        result.data.results.forEach((result) => {
          if (result.status === "processed") {
            const attemptData =
              roomJoinExceptionAttempts[result.conversationId];
            if (attemptData) {
              attemptData.count = 0;
            }
          }
        });
      }
    }

    return result;
  } catch (err) {
    handleApiError("conversation.handleRoomJoinExceptionBatch", err);
    console.debug("Batch room join exception processing failed", err);

    // Fallback: retry failed items individually if possible
    currentBatch.forEach((item) => {
      if (item.attemptData.count < MAX_RETRY_ATTEMPTS) {
        setTimeout(() => {
          console.debug(
            `Fallback individual retry for room ${item.conversationId}`
          );
          createRoomInChatService(item.conversationId, item.errCode);
        }, 2000); // Retry after 2 seconds
      }
    });

    return null;
  }
};

// Track rooms that are currently in the process of joining
const activeJoinRequests = new Set();

const joinRoom = async (socket, roomId) => {
  return new Promise((resolve, reject) => {
    if (!roomId) {
      return reject("Unable to join room: missing roomId");
    }

    // If socket isn't connected or doesn't exist, don't attempt to join
    if (!socket || socket.disconnected) {
      console.debug(
        `Cannot join room ${roomId} - socket is disconnected or null`
      );
      return resolve(null);
    }

    // Check if this room is already being processed to prevent duplicate requests
    if (activeJoinRequests.has(roomId)) {
      console.debug(
        `Room ${roomId} join already in progress, skipping duplicate request`
      );
      return resolve(null);
    }

    // Mark this room as having an active join request
    activeJoinRequests.add(roomId);

    socket.emit("roomJoin", roomId, async (_err, data) => {
      // Remove room from active requests set once the operation completes
      activeJoinRequests.delete(roomId);

      if (_err) {
        console.debug("roomJoin Err", _err.code, "for room", roomId);
        // Only attempt exception handling if we have a specific error code
        if (_err.code) {
          await createRoomInChatServiceBatch(roomId, _err.code);
        }
        return resolve(null);
      }

      return resolve(data);
    });
  });
};

const leaveRoom = (socket, roomId) => {
  return function (dispatch) {
    socket.emit("roomLeave", roomId, (err) => {
      if (err) {
        console.log(err);
        return;
      }
      dispatch(Actions.setConversationId(null));
    });
  };
};

const sendMessage = (socket, roomId, message) => {
  return new Promise((resolve, reject) => {
    if (!socket || socket.disconnected) {
      Sentry.withScope((scope) => {
        scope.setExtra("What Happened?", "Unable to send message in chat");
        Sentry.captureException(
          new Error(
            "No socket connection found, or socket.disconnected is true"
          )
        );
      });
      return reject("No Socket");
    }
    socket.emit("roomMessage", roomId, message, (err, data) => {
      if (err || !data) {
        Sentry.withScope((scope) => {
          scope.setExtra("What Happened?", "Unable to send message in chat");
          Sentry.captureException(new Error(err));
        });
        return reject("Message not Sent");
      }
      return resolve(data);
    });
  });
};

const setTypingState = (
  socket,
  isTyping,
  userName,
  fullName,
  roomId,
  topicId = null
) => {
  socket.emit("systemMessage", {
    type: "typing",
    typing: isTyping,
    userName,
    fullName,
    roomName: roomId,
    topicId,
  });
};

const notifyAnikaOnHuddleJoin = (
  socket,
  roomId,
  topicId,
  messageId,
  userAddress,
  userId,
  huddleOwnerId
) => {
  if (!socket || socket.disconnected) {
    return;
  }
  socket.emit("systemMessage", {
    type: "notifyAnikaOnHuddleJoin",
    userAddress,
    roomName: roomId,
    topicId,
    messageId,
    userId,
    huddleOwnerId,
  });
};

const notifyAnikaOnHuddleReject = (
  socket,
  roomId,
  topicId,
  messageId,
  userAddress,
  userId,
  huddleOwnerId
) => {
  if (!socket || socket.disconnected) {
    return;
  }
  socket.emit("systemMessage", {
    type: "notifyAnikaOnHuddleReject",
    userAddress,
    roomName: roomId,
    topicId,
    messageId,
    userId,
    huddleOwnerId,
  });
};

const sendMessageAcknowledgment = (socket, roomId, seenBy) => {
  const currentDate = Date.now();
  socket.emit("systemMessage", {
    type: "messageSeen",
    roomName: roomId,
    messageSeenAt: currentDate,
    messageSeenBy: seenBy,
  });
  return (dispatch) => {
    dispatch(Actions.updateLastSeen(roomId, currentDate));
  };
};

const getOrCreateGigConversation = (
  gigOrder = null,
  startAConversationFLow = false,
  callback = null
) => {
  if (gigOrder) {
    const payload = {
      gigOrder,
    };
    if (startAConversationFLow) {
      payload["trigger"] = "start_a_conversation_flow";
    }
    axios
      .post(`${API_URL}/v1/chat/conversation.createOrGetWithGigOrder`, payload)
      .then((res) => {
        if (res.data) {
          if (typeof callback === "function")
            callback({
              conversation: res.data.conversation,
              conversationId: res.data.conversation._id,
            });
        }
      })
      .catch((error) => {
        error?.response?.message
          ? alert(error.response.message)
          : console.log("Sorry, but we are unable to sync with backend");
      });
  }
};

const getOrCreateConversationWithKey = (payload, callback = null) => {
  if (Object.keys(payload).length) {
    axios
      .post(`${API_URL}/v1/chat/conversation.createWithKey`, payload)
      .then((res) => {
        if (res.data) {
          if (typeof callback === "function") {
            callback({
              conversation: res.data.conversation,
              conversationId: res.data.conversation._id,
            });
          }
        }
      })
      .catch((error) => {
        error?.response?.message
          ? alert(error.response.message)
          : console.log("Sorry, but we are unable to sync with backend");
      });
  }
};

const updateMessage = (conversationId, topicId, message) => {
  const currentState = store.getState();
  let messages = currentState.chat?.chatLogs[conversationId];
  if (topicId && messages) {
    messages = messages[topicId];
  }
  // Build a new array: patching the store's array in place made the change
  // invisible to memoised bubbles and to anything comparing references.
  const index =
    messages?.findIndex?.((_msg) => _msg?.messageId === message?.messageId) ??
    -1;
  if (index !== -1) {
    messages = messages.map((_msg, i) =>
      i === index ? { ..._msg, ...message } : _msg
    );
  }
  if (messages?.length) {
    return (dispatch) =>
      dispatch(Actions.setRoomMessages(conversationId, messages, topicId));
  }
  return (dispatch) => ({
    type: "",
  });
};

// Not to be dispatched
const getProviderId = (conversationId) => {
  const currentState = store.getState();
  const currentConversationsId =
    conversationId || currentState.chat.selectedConversationId;
  const currentConversation =
    currentState.chat?.conversations?.filter(
      (_conversation) => _conversation.conversationId === currentConversationsId
    )?.[0] || null;
  if (currentConversation?.conversation?.purpose?.gig?.listing?.provider) {
    return currentConversation.conversation.purpose.gig.listing.provider;
  }
  if (
    currentConversation?.conversation?.purpose?.classRoom?.gigListing?.provider
  ) {
    return currentConversation.conversation.purpose.classRoom.gigListing
      .provider;
  }
  return null;
};

// Not to be dispatched
const getParticipantsDetails = (participantsId) => {
  const currentState = store.getState();
  const currentConversationsId = currentState.chat.selectedConversationId;
  const participants =
    currentState.chat?.participants[currentConversationsId]?.list || [];
  const participantDetails =
    participants?.filter(
      (_participant) => _participant.participantId === participantsId
    )?.[0] || {};
  return participantDetails?.profile;
};

// Not to be dispatched
const getCredentials = (participantId) => {
  const profile = getParticipantsDetails(participantId);
  if (profile?.credentials && profile?.defaultCredential) {
    return profile.credentials[profile?.defaultCredential]?.headline;
  }
  return null;
};

// Not to be dispatched
const redirectToUserProfile = (participantId, username = null) => {
  if (username) {
    const win = window.open(`/profile/${username}`, "_blank");
    win?.focus();
  } else if (participantId) {
    const win = window.open(`/profile/id/${participantId}`, "_blank");
    win?.focus();
  }
  return null;
};

const updateConversationTopics = (
  conversationId,
  page = 0,
  limit = 10,
  search = ""
) => {
  return function (dispatch) {
    dispatch(Actions.loadingConversationTopics(conversationId, true));
    return axios
      .get(
        `${API_URL}/v1/chat/conversation.listTopics?conversationId=${conversationId}&search=${search}&page=${page}&limit=${limit}`
      )
      .then((res) => {
        if (res.data) {
          const { topics, currentCount } = res.data;
          dispatch(
            Actions.updateConversationTopics(
              conversationId,
              topics,
              currentCount
            )
          );
        }
      })
      .catch((error) => {
        error?.response?.message
          ? alert(error.response.message)
          : console.log("Sorry, but we are unable to sync with backend");
      })
      .finally(() => {
        dispatch(Actions.loadingConversationTopics(conversationId, false));
      });
  };
};

const MAIL_LOADING_SUFFIX = ":mail";

function mailHistory(conversationId, cursor, direction, limit = 30) {
  return function (dispatch) {
    const loadingKey = `${conversationId}${MAIL_LOADING_SUFFIX}`;
    dispatch(Actions.setMessageLoadingState(loadingKey, true));
    return axios
      .get(
        `${API_URL}/v1/chat/conversation.history?conversation=${conversationId}&filter=email&limit=${limit}${
          cursor ? "&cursor=" + cursor : ""
        }${direction ? "&direction=" + direction : ""}`
      )
      .then((res) => {
        if (res.data && res.data.history) {
          if (res.data.history.length) {
            const historyMessages = res.data.history.map((messageDoc) => ({
              ...messageDoc.content,
              messageId: messageDoc._id,
              rootCreatedAt: messageDoc.createdAt,
              metadata: {
                ...messageDoc.metadata,
                ...messageDoc.content.metadata,
              },
            }));
            if (!cursor) {
              dispatch(
                Actions.setMailMessages(conversationId, historyMessages)
              );
            } else {
              dispatch(
                Actions.updateMailHistory(conversationId, historyMessages)
              );
            }
          } else {
            dispatch(Actions.setAllMailMessagesLoaded(conversationId));
          }
        }
      })
      .catch((error) => {
        console.error("Failed to fetch mail history:", error);
        Sentry.captureException(error);
      })
      .finally(() => {
        dispatch(Actions.setMessageLoadingState(loadingKey, false));
      });
  };
}

const Operations = {
  conversationOpen,
  conversationList,
  shouldUpdateConversationList,
  conversationHistory,
  jumpToMessage,
  jumpToLatest,
  conversationMark,
  conversationMarkUnread,
  conversationParticipants,
  conversationParticipantsCount,
  conversationListTopics,
  conversationCreateTopic,
  sendMessage,
  joinRoom,
  leaveRoom,
  setTypingState,
  joinAllRooms,
  sendMessageAcknowledgment,
  getOrCreateGigConversation,
  getOrCreateConversationWithKey,
  getProviderId,
  getParticipantsDetails,
  updateMessage,
  getCredentials,
  redirectToUserProfile,
  updateConversationTopics,
  fetchConversationEmailParticipants,
  notifyAnikaOnHuddleJoin,
  notifyAnikaOnHuddleReject,
  mailHistory,
};

export default Operations;
